import BigNumber from 'bignumber.js'
import { trimEnd } from 'lodash'
import { OrderResponse, Protocol } from '@tokenlon/sdk'
import { FeeGears } from '~/apis'
import { toBigNumber, toBigNumberWithDecimal } from '~/utils/numbers'
import { PROTOCOL_TYPE, TradeDirection, GasLimitByProtocols } from './types'
import {
  TokenlonSDK,
  MAX_UINT160,
  CrossSwapOrderStatus,
} from '@tokenlon/v6-sdk'
import {
  Address,
  zeroAddress,
  createPublicClient,
  http,
  parseAbiItem,
  Hash,
  getAddress,
} from 'viem'
import dayjs from 'dayjs'
import { RateState } from './types'
import { Polygon, BSC } from '~/model/chain/ethereum'
import { getChainById } from '~/utils/chain'
import { compareString } from '~/utils/common'

export const isAMM = (protocol: string): boolean => {
  return protocol?.includes(PROTOCOL_TYPE.AMM)
}

const isPMMV5 = (protocol?: string) => {
  return protocol === Protocol.PMMV5
}

const isRFQV2 = (protocol?: string) => {
  return protocol === Protocol.RFQV2
}

export const gt = (value1, value2): boolean =>
  !!value1 && !!value2 && !!toBigNumber(value1).gt(toBigNumber(value2))

export const lt = (value1, value2): boolean =>
  !!value1 && !!value2 && !!toBigNumber(value1).lt(toBigNumber(value2))

/**
 * 用户输入时，使用该方法进行格式化
 *  1. 保留用户输入的小数点 `.`
 *  2. 确保用户输入的小数点位数不超过 precision
 */
export const formatInputAmount = (amount, precision) => {
  try {
    const amountStr = amount?.toString()
    const [ints, decs] = amountStr?.split('.')

    return decs ? `${ints}.${decs?.substring(0, precision)}` : amountStr
  } catch (e) {
    return amount
  }
}

// @todo: @circlehotarux 通过参数控制加入 Math.min(8, decimal ?? 8) 的计算
export const formatAmount = (amount: any = '', precision = 8) => {
  try {
    const amountStr = amount?.toString()
    const [ints, decs] = amountStr?.split('.')
    if (precision === 0) {
      return ints
    }
    let trimmedDecs = trimEnd(decs?.substring(0, precision), '0')
    if (
      Number(trimmedDecs) === 0 &&
      Number(decs) !== 0 &&
      decs?.length > precision
    )
      trimmedDecs = decs

    return trimmedDecs ? `${ints}.${trimmedDecs}` : ints
  } catch (e) {
    return amount
  }
}

/**
 * eth 兑 token，是自主上链的形式，交易会扣除一定 eth 作为矿工费
 *  1. 在用户输入 eth 数量时，我们需要预留下 estimated gas fee，与 app 对齐，使用 extra?.tokenlon?.maxFeePerGas，而不是 1.5倍 baseFeePerGas
 *  2. 在用户点击闪兑时，我们需要使用 real time gas fee 进行校验
 * token 兑 token 时，不需要预留矿工费，也不需要进行矿工费校验
 */
export const DEFAULT_GAS_LIMIT = 580000
const DEFAULT_MAX_PRIORITY_FEE_PER_GAS = 1500000000

const getMinEstimatedGasPrice = (feeGears: FeeGears): string => {
  const safeLow =
    feeGears?.gears.find((gear) => gear.speed === 'SAFE_LOW') ??
    feeGears?.gears.find((gear) => gear.speed === 'AVERAGE')
  if (safeLow) return toBigNumberWithDecimal(safeLow.gasPrice, 18).toString()
}

const getMaxEstimatedGasPrice = (feeGears: FeeGears): string =>
  toBigNumberWithDecimal(feeGears?.extra?.tokenlon?.maxFeePerGas, 18).toString()

export const calcGasFee = (
  gasPrice: string,
  gasLimit: number | string,
): string =>
  toBigNumber(gasPrice).multipliedBy(toBigNumber(gasLimit)).toString()

export const getEstimatedGasFee = (
  feeGears: FeeGears,
  gasLimits: GasLimitByProtocols | undefined,
  order: OrderResponse | undefined,
) => {
  const gasLimit = getEstimatedGasLimitPerProtocol({
    gasLimits,
    protocol: order?.order?.protocol ?? '',
  })
  const baseGasLimit = getEstimatedGasLimitPerProtocol({
    gasLimits: { ...gasLimits, bufferFactor: 1 },
    protocol: order?.order?.protocol ?? '',
  })
  const maxFeePerGas = getMaxEstimatedGasPrice(feeGears)
  const safeLowGasPrice = getMinEstimatedGasPrice(feeGears)

  const maxGasPrice = maxFeePerGas ?? '0'
  const minGasPrice = safeLowGasPrice ?? '0'

  const maxEstimatedGasFee = calcGasFee(maxGasPrice, gasLimit)
  const minEstimatedGasFee = calcGasFee(minGasPrice, baseGasLimit)
  return { maxEstimatedGasFee, minEstimatedGasFee }
}

export const getEthTxOptions = (
  feeGears: FeeGears,
  gasLimits: GasLimitByProtocols | undefined,
  order: OrderResponse | undefined,
) => {
  const gasLimit = getEstimatedGasLimitPerProtocol({
    gasLimits,
    protocol: order?.order?.protocol ?? '',
  })
  const formatedGasLimit = toBigNumber(gasLimit).toFixed(0)
  const maxFeePerGas = feeGears?.extra?.tokenlon?.maxFeePerGas ?? '0'
  const maxPriorityFeePerGas = DEFAULT_MAX_PRIORITY_FEE_PER_GAS.toString()
  return { gasLimit: formatedGasLimit, maxFeePerGas, maxPriorityFeePerGas }
}

export const calcAmounts = (
  inputToken: SpotSwapToken,
  outputToken: SpotSwapToken,
  mode: TradeDirection,
  order: OrderResponse,
  slippage: number, // 范围 1 ~ 99
): {
  fee: bigint
  gasFee: bigint
  tradeFee: bigint
  makerTokenAmount: bigint
  minimum: bigint
  tradeFeeRate: number
} => {
  if (!order || !order?.order || !inputToken || !outputToken) return
  // decimal 用来确定小数点位置
  const inputDecimal = inputToken?.decimals
  const outputDecimal = outputToken?.decimals

  // precision 用来确定小数点精度
  // @notice: from @longhai 输入方数量使用 precision 计算；对手方/最小收到数量/矿工费等使用 decimal 计算，最多 8 位
  // 此处 inputPrecision 仅用于计算对手方数量，因此使用 inputDecimal
  const inputPrecision = Math.min(8, inputDecimal ?? 8)
  const outputPrecision = Math.min(8, outputDecimal ?? 8)

  const fee = toBigNumber(order?.order?.makerAssetAmount)
    .multipliedBy(order?.order?.feeFactor)
    .dividedBy(10000)
  const gasFee = toBigNumber(order?.order?.makerAssetAmount)
    .multipliedBy(order?.order?.feeDetail?.gasFee)
    .dividedBy(10000)
  const countTradeFee = fee.minus(gasFee)
  const tradeFee = countTradeFee.lte(0) ? toBigNumber(0) : countTradeFee
  const tradeFeeRate = toBigNumber(order?.order?.feeDetail?.tokenlonFee)
    .dividedBy(100)
    .toFormat()

  const minimum = toBigNumber(order?.order?.makerAssetAmount)
    .multipliedBy(new BigNumber(10000).minus(order?.order?.feeFactor))
    .dividedBy(10000)
    .multipliedBy(new BigNumber(100).minus(slippage ?? 0))
    .dividedBy(100)
  // 目前只处理 input 方向，不进行 format，和 v6 保持统一在外层处理
  if (mode === TradeDirection.Input) {
    const income = toBigNumber(order?.order?.makerAssetAmount)
    return {
      fee: BigInt(fee.toFixed(0)),
      gasFee: BigInt(gasFee.toFixed(0)),
      tradeFee: BigInt(tradeFee.toFixed(0)),
      makerTokenAmount: BigInt(income.toFixed(0)),
      minimum: BigInt(minimum.toFixed(0)),
      tradeFeeRate: Number(tradeFeeRate) * 100, // 使用万分比
    }
  }
}

export const validateInputAmount = ({
  balance,
  inputToken,
  inputAmount,
  order,
  t,
  returnRawError = false,
}) => {
  const insufficientBalanceError = returnRawError
    ? '$symbol_insufficient_balance'
    : t('$symbol_insufficient_balance', { symbol: inputToken?.symbol })
  if (!balance) {
    return insufficientBalanceError
  }
  if (!inputToken || !inputAmount || !order) return
  if (gt(inputAmount, balance)) return insufficientBalanceError
}

export const validateETHBalance = ({
  balance,
  gasFee,
  takerToken,
  takerTokenAmountUnit,
  t,
  isTakerETH,
  handleInsufficientBalance,
  returnRawError = false,
  disableSetPayMode,
}) => {
  const insufficientBalanceError = returnRawError
    ? '$symbol_insufficient_balance'
    : t('$symbol_insufficient_balance', { symbol: takerToken?.symbol })
  if (!balance) {
    return insufficientBalanceError
  }
  const cost = isTakerETH
    ? toBigNumber(takerTokenAmountUnit).plus(toBigNumber(gasFee))
    : toBigNumber(gasFee)

  if (gt(cost, balance)) {
    if (isTakerETH || disableSetPayMode) {
      return insufficientBalanceError
    } else {
      handleInsufficientBalance()
      return 'insufficient_balance_with_toggle_modal'
    }
  }
}

export const validateInputAmountSendBySelf = ({
  balance,
  inputToken,
  inputAmount,
  order,
  t,
  ethBalance,
  gasFee,
  isTakerETH,
  handleInsufficientBalance,
  // 预览按钮的文案需要特殊处理，增加字段控制返回原始的错误信息
  returnRawError = false,
  isHoT,
  disableSetPayMode,
}) => {
  if (!isHoT) {
    const error = validateETHBalance({
      balance: ethBalance,
      gasFee,
      takerToken: inputToken,
      takerTokenAmountUnit: inputAmount,
      t,
      isTakerETH,
      handleInsufficientBalance,
      returnRawError,
      disableSetPayMode,
    })
    if (error) return error
  }

  if (!inputToken || !inputAmount || !order) return

  if (gt(inputAmount, balance))
    return returnRawError
      ? '$symbol_insufficient_balance'
      : t('$symbol_insufficient_balance', { symbol: inputToken?.symbol })
}

export const getEstimatedGasLimitPerProtocol = ({
  gasLimits,
  protocol,
}: {
  gasLimits?: GasLimitByProtocols
  protocol?: string
}): number => {
  const {
    pmm = 0,
    sushiswap: sushiSwap = 0,
    uniswap = 0,
    rfqv2 = 0,
    bufferFactor = 1,
  } = gasLimits || {}

  // 链上找不到任何交易时，pmm会回传 50w 的预设值，这个时候不要乘上 bufferFactor
  let pmmBN = toBigNumber(pmm)
  if (!pmmBN.eq(toBigNumber(DEFAULT_GAS_LIMIT)))
    pmmBN = pmmBN.times(bufferFactor)
  const sushiSwapBN = toBigNumber(sushiSwap).times(bufferFactor)
  const uniswapBN = toBigNumber(uniswap).times(bufferFactor)
  const rfqv2BN = toBigNumber(rfqv2).times(bufferFactor)

  if (isPMMV5(protocol) && pmm) return pmmBN.toNumber()
  // protocol 是 AMM 时，取被返回的 sushiSwap 和 uniswap 中的较大的一个作为 gasLimit
  if (isAMM(protocol) && (sushiSwap || uniswap))
    return BigNumber.max(sushiSwapBN, uniswapBN).toNumber()
  if (isRFQV2(protocol) && rfqv2) return rfqv2BN.toNumber()
  // protocol 未知，取被返回的 pmm, sushiSwap, uniswap 中最大的一个作为 gasLimit
  if (pmm || sushiSwap || uniswap || rfqv2)
    return BigNumber.max(pmmBN, sushiSwapBN, uniswapBN, rfqv2BN).toNumber()
  return DEFAULT_GAS_LIMIT
}

export const showSkeletonStateList = [
  RateState.LOADING,
  RateState.IDLE,
  RateState.NetworkReason,
]

export const getTokenPermit = async ({
  account,
  spender,
  tokenAddress,
  sdk,
}: {
  account: Address
  spender: Address
  tokenAddress: Address
  sdk: TokenlonSDK
}) => {
  const { nonce } = await sdk.permit.getTokenPermit({
    account,
    token: tokenAddress,
    spender: spender,
  })
  const permitMessage = {
    details: {
      token: tokenAddress,
      amount: MAX_UINT160,
      nonce,
      // expiration when the allowance need to be reset
      expiration: dayjs().add(1, 'month').unix(),
    },
    spender: spender,
    sigDeadline: BigInt(dayjs().add(30, 'minute').unix()),
  }
  const permit = await sdk.permit.signTakerPermit(permitMessage, account)
  return permit
}

const isPolygonOrBSC = (chainId: number) => {
  return chainId === Polygon.chainId || chainId === BSC.chainId
}

/**
 * 如果 inputToken 是 ETH 或 WETH，会有以下情况：
 * 1. recipient 是合约钱包，跨链收到的代币是 WETH，crossChain 只保留 destinationToken 是 WETH 的路径；
 * 2. recipient 是 EOA 钱包，跨链收到的代币是 ETH，crossChain 只保留 destinationToken 是 ETH 的路径；
 * 3. ETH 和 WETH 返回的 crossChain 属性同时包含 ETH 和 WETH 路径。
 * 特殊情况：
 * 1. 目标链是 polygon 或 bsc 时，recipient 不管是什么类型，收到的都是 WETH；
 * 2. 源链是 polygon 或 bsc 时，Across 只返回 WETH 路径；
 * 3. bsc 上的 ETH 0x2170ed0880ac9a755fd29b2688956bd959f933f8 是 Across 的 WETH，前后端也将该代币当做 WETH 处理。
 */
export const formatETHCrossChain = (
  crossChain: CrossChain,
  isContractAddress: boolean,
): CrossChain => {
  if (!crossChain) return []

  let filteredResult: CrossChain = []
  if (isContractAddress) {
    filteredResult = crossChain.filter(
      (item) => item.destinationTokenSymbol === 'WETH',
    )
  } else {
    filteredResult = crossChain.filter((item) => {
      if (isPolygonOrBSC(item.originChainId)) {
        // 源链是 polygon 或 bsc 时，Across 只返回 WETH 路径，不需要过滤
        return true
      } else if (isPolygonOrBSC(item.destinationChainId)) {
        // 目标链是 polygon 或 bsc 时，只有 WETH 路径有用
        return item.destinationTokenSymbol === 'WETH'
      } else {
        return item.destinationTokenSymbol === 'ETH'
      }
    })
  }
  // crossChain 属性来自 Across，Across 侧 ETH 地址返回的是 WETH 地址，需要做转化
  const formattedResult = filteredResult.map((item) => {
    // 源链是 polygon 或 bsc 时，只有 WETH 路径，但 EOA 地址收到的是 ETH，需要将地址转化成 Tokenlon 的 ETH 地址。但如果目标链是 polygon 或 bsc，则不需要转化
    if (
      !isContractAddress &&
      isPolygonOrBSC(item.originChainId) &&
      !isPolygonOrBSC(item.destinationChainId) &&
      item.destinationTokenSymbol === 'WETH'
    ) {
      return { ...item, destinationToken: zeroAddress }
    } else if (item.destinationTokenSymbol === 'ETH') {
      return { ...item, destinationToken: zeroAddress }
    } else {
      return item
    }
  })
  return formattedResult
}

const getClient = (chainId: number) => {
  const chain = getChainById(chainId)
  // https://eth-mainnet.token.im is not allowed to use eth_getLogs, so we use publicRpcUrl
  const rpcUrl = chain.publicRpcUrl ?? chain.rpcUrl
  return createPublicClient({
    transport: http(rpcUrl),
  })
}

const bytes32ToAddress = (bytes32: string): Address => {
  const address = `0x${bytes32.slice(-40)}`
  return getAddress(address)
}

const trackOriginTx = async ({
  fromChainId,
  fromTxHash,
  spokePoolAddress,
}: {
  fromChainId: number
  fromTxHash: string
  spokePoolAddress: Address
}) => {
  const fromClient = getClient(fromChainId)
  const receipt = await fromClient.waitForTransactionReceipt({
    hash: fromTxHash as Hash,
  })
  if (receipt.status === 'success') {
    const logs = receipt.logs
    // It's possible that the Across contract upgrade resulted in the depositId not being found.
    const FundsDepositedEvent = logs.find(({ address }) =>
      compareString(address, spokePoolAddress),
    )
    const depositId = FundsDepositedEvent?.topics?.[2]

    if (depositId) {
      console.log('FundsDepositedEvent depositId: ', BigInt(depositId))
      return {
        orderStatus: CrossSwapOrderStatus.OriginSuccess,
        depositId: BigInt(depositId),
      }
    } else {
      return {
        orderStatus: CrossSwapOrderStatus.OriginSuccess,
      }
    }
  } else {
    return {
      orderStatus: CrossSwapOrderStatus.OriginFailed,
    }
  }
}

const TRACKING_TIMEOUT_MS = 6000
const POLLING_INTERVAL_MS = 1000
const FilledRelayEvent = parseAbiItem(
  'event FilledRelay(bytes32 inputToken, bytes32 outputToken, uint256 inputAmount, uint256 outputAmount, uint256 repaymentChainId, uint256 indexed originChainId, uint256 indexed depositId, uint32 fillDeadline, uint32 exclusivityDeadline, bytes32 exclusiveRelayer, bytes32 indexed relayer, bytes32 depositor, bytes32 recipient, bytes32 messageHash, (bytes32 updatedRecipient, bytes32 updatedMessageHash, uint256 updatedOutputAmount, uint8 fillType) relayExecutionInfo)',
)

export const trackCrossChainTx = async ({
  fromTxHash,
  fromChainId,
  toChainId,
  recipient,
  spokePoolAddress,
  setOrderStatus,
  setIsTrackingCrossTxByEvent,
}: {
  fromTxHash: string
  fromChainId: number
  toChainId: number
  recipient: string
  spokePoolAddress: Address
  setOrderStatus: (status: CrossSwapOrderStatus) => void
  setIsTrackingCrossTxByEvent: (value: boolean) => void
}) => {
  try {
    let matchedEvent
    let depositId: bigint

    const toClient = getClient(toChainId)
    const startBlockNumber = await toClient.getBlockNumber()

    try {
      const { orderStatus, depositId: originDepositId } = await trackOriginTx({
        fromChainId,
        fromTxHash,
        spokePoolAddress,
      })

      setOrderStatus(orderStatus)

      if (
        orderStatus === CrossSwapOrderStatus.OriginSuccess &&
        !!originDepositId
      ) {
        depositId = originDepositId
      }
    } catch (error) {
      console.error('failed to get receipt', error)
    }

    if (!depositId) {
      setIsTrackingCrossTxByEvent(false)
      return
    }

    let timeout = false
    const timeoutId = setTimeout(() => {
      timeout = true
      setIsTrackingCrossTxByEvent(false)
    }, TRACKING_TIMEOUT_MS)

    while (!timeout && !matchedEvent) {
      const latestBlockNumber = await toClient.getBlockNumber()

      // Why not use publicClient.watchEvent(eth_newFilter)? Because some public RPCs restrict the use of this method. For example, https://mainnet.optimism.io returns 'rpc method is not whitelisted'.
      const logs = await toClient.getLogs({
        fromBlock: startBlockNumber,
        toBlock: latestBlockNumber,
        address: spokePoolAddress,
        event: FilledRelayEvent,
      })

      const matched = logs.find(({ args }) => {
        const logFromChainId = Number(args.originChainId)
        const logRecipient = bytes32ToAddress(args.recipient)
        const logDepositId = args.depositId

        return (
          logDepositId === depositId &&
          logFromChainId === fromChainId &&
          logRecipient === recipient
        )
      })

      if (matched) {
        console.log('matched event', matched)
        console.timeEnd('trackTxByFrontend')
        matchedEvent = matched
        clearTimeout(timeoutId)
        setOrderStatus(CrossSwapOrderStatus.DestinationSuccess)
        break
      }

      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS))
    }
  } catch (error) {
    console.error('failed to track cross chain tx', error)
    setIsTrackingCrossTxByEvent(false)
  }
}
